import { AssistantAction, AssistantContext, AssistantResponse } from '../types/AssistantTypes';
import { recipeContextManager } from '../../recipeContextService';
import { ImageGenerationModalService } from '../../imageModalService';
import { formatErrorForUser } from '@/utils/errorUtils';
import { safeUrl } from '../security';
import { writePendingRecipe } from '@/lib/pendingRecipe';

/**
 * Enhanced centralized action button handler
 * Preserves all working functionality while adding type safety
 */
export class ButtonActionHandler {
  
  /**
   * Execute action with enhanced type safety and error handling
   * This preserves all existing functionality from AssistantWidget
   */
  static async executeAction(
    action: AssistantAction,
    dataAttrs: Record<string, string>,
    context: AssistantContext,
    uiCallbacks: {
      addMessage: (conversationId: string, message: any) => void;
      playDoneSound: () => void;
      onOpenStudio?: () => void;
      scrollToBottom: () => void;
    }
  ): Promise<AssistantResponse> {
    
    const { addMessage, playDoneSound, onOpenStudio } = uiCallbacks;
    const conversationId = context.activeConversationId;
    
    if (!conversationId) {
      return {
        success: false,
        error: 'No active conversation ID available'
      };
    }

    
    try {
      switch (action) {
        case AssistantAction.EDIT_IMAGE_PROMPT:
          return await this.handleEditImagePrompt(dataAttrs, addMessage, conversationId);
          
        case AssistantAction.DOWNLOAD_HIGHRES:
          return await this.handleDownloadHighres(dataAttrs, addMessage, conversationId);
          
        case AssistantAction.GENERATE_RECIPE_IMAGE:
          return await this.handleGenerateRecipeImage(addMessage, conversationId, context);
          
        case AssistantAction.OPEN_RECIPE_STUDIO:
          return await this.handleOpenRecipeStudio(addMessage, conversationId, onOpenStudio, playDoneSound);
          
        case AssistantAction.CONFIRM_RECIPE_IMPORT:
          return await this.handleConfirmRecipeImport(dataAttrs, addMessage, conversationId, playDoneSound);
          
        case AssistantAction.OPEN_PASTE_FORM:
          return await this.handleOpenPasteForm(addMessage, conversationId);
          
        default:
          throw new Error(`Unhandled action: ${action}`);
      }
      
    } catch (error: any) {
      
      const errorMessage = `❌ Error: ${formatErrorForUser(error)}`;
      addMessage(conversationId, {
        content: errorMessage,
        isUser: false,
        text: errorMessage,
        sender: 'assistant'
      });
      
      return {
        success: false,
        error: formatErrorForUser(error)
      };
    }
  }
  
  /**
   * Handle edit image prompt action
   * Creates an editable prompt interface using the existing EDITABLE_PROMPT system
   */
  private static async handleEditImagePrompt(
    dataAttrs: Record<string, string>,
    addMessage: (...args: any[]) => any,
    conversationId: string
  ): Promise<AssistantResponse> {
    
    const { recipeContextManager } = await import('../../recipeContextService');
    const currentRecipe = recipeContextManager.getCurrentRecipe();
    
    if (!currentRecipe) {
      addMessage(conversationId, {
        content: "❌ No recipe found. Please open a recipe first.",
        isUser: false,
        text: "No recipe found",
        sender: 'assistant'
      });
      
      return { success: false, error: 'No recipe context' };
    }

    try {
      // UPDATED: Instead of generating prompt here (which wastes LLM calls),
      // just open the modal with editing mode enabled
      const { ImageGenerationModalService } = await import('../../imageModalService');
      
      // Open the modal which will handle all prompt generation efficiently
      ImageGenerationModalService.openModal(currentRecipe, 'assistant');
      
      const response = `🎨 **I've opened the image generator for you!**

The image generation modal is now open where you can:
✨ Generate professional food photos using AI
🎯 See your current recipe image for comparison  
✏️ **Customize the prompt if needed**
💫 Save the perfect image directly to your recipe

Click "Customize Prompt" in the modal to edit the AI-generated prompt, or generate directly with the optimized prompt.`;

      addMessage(conversationId, {
        content: response,
        isUser: false,
        text: response,
        sender: 'assistant',
        metadata: {
          type: 'image-modal-opened',
          recipeId: currentRecipe.id
        }
      });

      return { success: true, message: 'Image generation modal opened with edit capabilities' };

    } catch {
      const errorMessage = "❌ Failed to prepare prompt editor. Please try again.";
      addMessage(conversationId, {
        content: errorMessage,
        isUser: false,
        text: errorMessage,
        sender: 'assistant'
      });
      
      return { success: false, error: 'Failed to create edit interface' };
    }
  }

  /**
   * Handle download high-resolution image action
   */
  private static async handleDownloadHighres(
    dataAttrs: Record<string, string>,
    addMessage: (...args: any[]) => any,
    conversationId: string
  ): Promise<AssistantResponse> {
    
    const highresData = dataAttrs['highres-url']; // This now contains the Base64 data
    const filename = dataAttrs['filename'] || 'recipe_image.jpg';

    if (highresData) {
      try {
        // Preserve download logic from original
        const link = document.createElement('a');
        link.href = safeUrl(highresData); // The Base64 data URI
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // No chat message needed for a successful download, as the browser handles it.
        return {
          success: true,
          message: 'Download initiated',
          metadata: { type: 'download' }
        };
        
      } catch {
        
        const errorMessage = '❌ Failed to initiate download.';
        addMessage(conversationId, {
          content: errorMessage,
          isUser: false,
          text: 'Failed to initiate download.',
          sender: 'assistant'
        });
        
        return {
          success: false,
          error: 'Failed to initiate download.'
        };
      }
    } else {
      const errorMessage = '❌ High-resolution image data not available.';
      addMessage(conversationId, {
        content: errorMessage,
        isUser: false,
        text: 'High-resolution image data not available.',
        sender: 'assistant'
      });
      
      return {
        success: false,
        error: 'High-resolution image data not available.'
      };
    }
  }
  
  /**
   * Handle generate recipe image action
   * Updated to use the new unified image generation modal
   */
  private static async handleGenerateRecipeImage(
    addMessage: (...args: any[]) => any,
    conversationId: string,
    _context: AssistantContext
  ): Promise<AssistantResponse> {
    
    // Get recipe context for image generation in edit mode
    const currentRecipe = recipeContextManager.getCurrentRecipe();
    
    if (currentRecipe) {
      // Open the new unified image generation modal
      ImageGenerationModalService.openModal(currentRecipe, 'assistant');
      
      // Add confirmation message that modal was opened
      addMessage(conversationId, {
        content: `🎨 **I've opened the image generator for "${currentRecipe.title}"!**

The image generation modal is now open where you can:
✨ Generate professional food photos using AI
🎯 See your current recipe image for comparison  
✏️ Customize the prompt if needed
💫 Save the perfect image directly to your recipe

The modal will automatically create a high-quality prompt based on your recipe details.`,
        isUser: false,
        text: "I've opened the image generator for you!",
        sender: 'assistant',
        metadata: {
          type: 'image-modal-opened'
        }
      });
      
      return {
        success: true,
        message: "Image generation modal opened",
        metadata: {
          type: 'image-modal-opened'
        }
      };
    } else {
      const errorMessage = "❌ No recipe found. Please open a recipe first to generate an image.";
      addMessage(conversationId, {
        content: errorMessage,
        isUser: false,
        text: "No recipe found",
        sender: 'assistant'
      });
      
      return {
        success: false,
        error: 'No recipe context available for image generation.'
      };
    }
  }
  
  /**
   * Handle open recipe studio action
   * Preserves existing functionality from AssistantWidget
   */
  private static async handleOpenRecipeStudio(
    addMessage: (...args: any[]) => any,
    conversationId: string,
    onOpenStudio?: (...args: any[]) => any,
    playDoneSound?: (...args: any[]) => any
  ): Promise<AssistantResponse> {
    
    // Open the Recipe Creation Studio
    
    if (onOpenStudio) {
      onOpenStudio();
      
      // Preserve completion sound from original
      if (playDoneSound) {
        playDoneSound();
      }
      
      // Add confirmation message (preserve original behavior)
      addMessage(conversationId, {
        content: "🎨 Opening Recipe Creation Studio...",
        isUser: false,
        text: "Opening Recipe Creation Studio...",
        sender: 'assistant'
      });
      
      return {
        success: true,
        message: 'Opening Recipe Creation Studio...',
        metadata: { type: 'studio-open' }
      };
      
    } else {
      
      const errorMessage = "❌ Unable to open Recipe Studio. Please try the studio button in the header.";
      addMessage(conversationId, {
        content: errorMessage,
        isUser: false,
        text: "Unable to open Recipe Studio.",
        sender: 'assistant'
      });
      
      return {
        success: false,
        error: 'Unable to open Recipe Studio.'
      };
    }
  }

  /**
   * Handle open paste form action
   * Navigates to the recipe import form
   */
  private static async handleOpenPasteForm(
    addMessage: (...args: any[]) => any,
    conversationId: string
  ): Promise<AssistantResponse> {
    
    
    addMessage(conversationId, {
      content: "📝 Opening the recipe import form...",
      isUser: false,
      text: "Opening recipe form...",
      sender: 'assistant',
      metadata: { type: 'open-paste-form' }
    });
    
    window.location.href = '/recipe/new/form';
    
    return {
      success: true,
      message: 'Recipe form opened',
      metadata: { type: 'open-paste-form' }
    };
  }

  /**
   * Handle confirm recipe import action
   * Parses the raw recipe text and navigates to the recipe form
   */
  private static async handleConfirmRecipeImport(
    dataAttrs: Record<string, string>,
    addMessage: (...args: any[]) => any,
    conversationId: string,
    playDoneSound?: (...args: any[]) => any
  ): Promise<AssistantResponse> {
    
    
    // Read raw text from sessionStorage (stored during the extraction step)
    const rawText = sessionStorage.getItem('pendingImportText');
    
    if (!rawText) {
      const errorMessage = "❌ No recipe text available. Please paste the recipe again.";
      addMessage(conversationId, {
        content: errorMessage,
        isUser: false,
        text: "No recipe text available",
        sender: 'assistant'
      });
      
      return {
        success: false,
        error: 'No recipe text available for import'
      };
    }
    
    // Add a loading message
    addMessage(conversationId, {
      content: "⏳ Parsing your recipe...",
      isUser: false,
      text: "Parsing your recipe...",
      sender: 'assistant',
      metadata: { type: 'loading' }
    });
    
    try {
      const { handleRecipeCreationRequest } = await import('../../recipeService');
      const result = await handleRecipeCreationRequest(rawText);
      
      // Clean up the pending text
      sessionStorage.removeItem('pendingImportText');
      
      if (result.success && result.data) {
        // Store the handoff in the wrapped format the form expects (with auto-generate flag)
        const formDataWithFlags = {
          ...result.data.formData,
          autoGenerateImage: true,
          fromAssistant: true
        };
        writePendingRecipe(formDataWithFlags);
        
        // Add success message
        addMessage(conversationId, {
          content: `✅ Recipe structured successfully! I've opened it in the editor — review the details and save when ready.`,
          isUser: false,
          text: "Recipe structured successfully!",
          sender: 'assistant',
          metadata: { type: 'recipe-import-confirm' }
        });
        
        if (playDoneSound) {
          playDoneSound();
        }
        
        // Navigate to the recipe form
        window.location.href = '/recipe/new/form';
        
        return {
          success: true,
          message: 'Recipe parsed and form opened',
          metadata: { type: 'recipe-import-confirm' }
        };
      } else {
        const errorMsg = result.error || 'Failed to parse the recipe.';
        addMessage(conversationId, {
          content: `❌ ${errorMsg}`,
          isUser: false,
          text: errorMsg,
          sender: 'assistant'
        });
        
        return {
          success: false,
          error: errorMsg
        };
      }
    } catch (error: any) {
      sessionStorage.removeItem('pendingImportText');
      const errorMessage = "❌ An error occurred while parsing the recipe. Please try again.";
      addMessage(conversationId, {
        content: errorMessage,
        isUser: false,
        text: errorMessage,
        sender: 'assistant'
      });
      
      return {
        success: false,
        error: error.message || 'Unexpected error during recipe import'
      };
    }
  }
  
  /**
   * Validate action type with enhanced type safety
   */
  static isValidAction(action: string): action is AssistantAction {
    return Object.values(AssistantAction).includes(action as AssistantAction);
  }
  
  /**
   * Get all available actions
   */
  static getAvailableActions(): AssistantAction[] {
    return Object.values(AssistantAction);
  }
}